import { useQueryClient } from '@tanstack/react-query';
import { css } from 'aphrodite';
import { ReactNode, useEffect, useId, useRef } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { toast } from 'sonner';
import {
	AIChatMessage,
	AIChatRequest,
	AIChatRole,
	AIMessageRequest,
	AITextContent,
	IOperationResultNoValue,
} from '../../../extViewmodels';
import { SalesLeadAIContext } from '../../../extViewmodels/sdk/models/AI';
import { useEventLogging } from '../../../models/Logging';
import { useAIAssistantCreateConversationMutation } from '../../../queries/AI/useAIAssistantCreateConversationMutation';
import {
	getConversationChatMessageQueryKey,
	useAIConversationChatMessageQuery,
} from '../../../queries/AIConversation/useAIConversationChatMessageQuery';
import { invalidateAllAIConversationFilterQueries } from '../../../queries/AIConversation/useAIConversationFilterQuery';
import { AIMarkdownResponse } from '../../../web/components-v2/ai/AiMarkdownResponse';
import {
	ChatConversation,
	ChatConversationContent,
	ChatConversationScrollButton,
} from '../../../web/components-v2/ai/ChatConversation';
import { LoadingSpinner } from '../../../web/components/LoadingSpinner';
import { WarningIcon } from '../../../web/components/svgs/icons/WarningIcon';
import {
	AIChatAssistantMessage,
	AIChatAssistantMessageError,
	AIChatUserMessage,
	removeErrorMessageFromConversationCache,
	useAIAssistantStreamingMutation,
} from '../../../web/containers/templates/AssistantChat';
import { bs } from '../../../web/styles/styles';
import { useErrorMessages } from '../../hooks';
import { LeadServedSource } from '../../viewModels/queue';
import { s } from './styles';

interface AIChatMessageError extends AIChatMessage {
	error: string;
	chatRequest: AIChatRequest;
}

const initialPromptMessage: AIChatMessage = {
	role: AIChatRole.User,
	text: 'Tell me about this lead',
	id: null,
};

const ASSISTANT_NAME = 'SalesPreCallAgent';

function PreCallAssistantChatLayout({ children }: { children: ReactNode }) {
	return (
		<div className={css(s.chatWindow)}>
			<div className={css(s.chatWindowHeader)}>
				<div className={css(s.chatWindowHeaderStart)}>
					<span className={css(s.chatWindowHeaderTitle)}>Pre Call Information</span>
				</div>
			</div>
			<div className={css(s.chatContent)}>{children}</div>
		</div>
	);
}

export function PreCallAssistantChatErrorBoundary({ children }: { children: ReactNode }) {
	const logger = useEventLogging('PreCallAssistantChatErrorBoundary');
	return (
		<ErrorBoundary
			fallback={
				<PreCallAssistantChatLayout>
					<div className={css(bs.flex, bs.flexCol, bs.itemsCenter, bs.justifyCenter, bs.p2, bs.hFull)}>
						<WarningIcon />
						<div className={css(bs.fontBold, bs.textBrandPrimary, bs.mt4)}>
							Error occurred loading pre-call information
						</div>
					</div>
				</PreCallAssistantChatLayout>
			}
			onError={error => {
				logger.logApiError('PreCallAssistantChatErrorBoundary-Error', error as IOperationResultNoValue);
			}}
		>
			{children}
		</ErrorBoundary>
	);
}

function AssistantChatInput({
	placeholder = 'Ask me anything...',
	onSubmit,
	streamingInProgress = false,
}: {
	placeholder?: string;
	onSubmit: (prompt: string) => void;
	streamingInProgress: boolean;
}) {
	const textAreaRef = useRef<HTMLTextAreaElement>(null);
	useEffect(() => {
		const adjustHeight = () => {
			textAreaRef.current.style.height = 'auto';
			textAreaRef.current.style.height = `${textAreaRef.current.scrollHeight + 2}px`;
		};
		textAreaRef.current?.addEventListener('input', adjustHeight);
	}, []);
	const handlePromptKeyPress = (ev: React.KeyboardEvent<HTMLTextAreaElement>) => {
		if (ev.key === 'Enter' && !ev.shiftKey && !ev.nativeEvent.isComposing) {
			ev.preventDefault();
			const prompt = textAreaRef.current.value.trim();
			if (prompt === '') {
				return;
			}
			if (streamingInProgress) {
				toast.error('Please wait for the current response to finish.');
				return;
			}
			onSubmit(prompt);
			textAreaRef.current.value = '';
		}
	};
	return (
		<textarea
			ref={textAreaRef}
			placeholder={placeholder}
			onKeyPress={handlePromptKeyPress}
			className={css(s.promptInput)}
		/>
	);
}

export function PreCallAssistantChat({
	companyId,
	leadServedId,
	source,
}: {
	companyId: string;
	leadServedId: string;
	source: LeadServedSource;
}) {
	const id = useId();
	const queryClient = useQueryClient();
	const errorMessages = useErrorMessages();
	const logger = useEventLogging('UniversalAssistantChat');
	const { mutate: createAIConversation, data: aiConversation } = useAIAssistantCreateConversationMutation({
		onSuccess: data => {
			invalidateAllAIConversationFilterQueries();
			aiAssistantStreamingMutation.mutate({
				conversationId: data.id,
				chatRequest: {
					contexts: [
						{
							_type: 'SalesLeadAIContext',
							companyId,
						} as SalesLeadAIContext,
					],
					chatMessages: [initialPromptMessage],
				} as AIChatRequest,
			});
		},
		onError: error => {
			errorMessages.pushApiError(error);
			logger.logApiError(`Create-SiteWide-Assistant-Error`, error);
		},
	});
	const aiConversationChatMessageQuery = useAIConversationChatMessageQuery({
		enabled: Boolean(aiConversation),
		id: aiConversation?.id,
		initialData: [initialPromptMessage],
	});
	useEffect(() => {
		createAIConversation({
			assistantName: ASSISTANT_NAME,
			companyId,
			leadServedId,
			source,
		});
	}, [createAIConversation, companyId, leadServedId, source]);

	const aiAssistantStreamingMutation = useAIAssistantStreamingMutation({
		assistantName: ASSISTANT_NAME,
	});
	const handleTryAgain = (chatRequest: AIChatRequest) => {
		removeErrorMessageFromConversationCache(aiConversation.id);
		aiAssistantStreamingMutation.mutate({
			conversationId: aiConversation.id,
			chatRequest,
		});
	};
	const handlePromptSubmit = (prompt: string) => {
		removeErrorMessageFromConversationCache(aiConversation?.id);
		queryClient.setQueryData(
			getConversationChatMessageQueryKey({
				id: aiConversation.id,
			}),
			(prevMessages: AIChatMessage[]) => [...(prevMessages ?? []), { role: AIChatRole.User, text: prompt, id: null }]
		);
		aiAssistantStreamingMutation.mutate({
			conversationId: aiConversation.id,
			chatRequest: {
				chatMessages: [
					{
						role: AIChatRole.User,
						text: prompt,
						id: null,
					},
				],
			} as AIMessageRequest,
		});
	};
	return (
		<PreCallAssistantChatLayout>
			<ChatConversation>
				<ChatConversationContent>
					{aiConversationChatMessageQuery.data?.map((message, i) => {
						if (message.role === AIChatRole.User) {
							return <AIChatUserMessage key={`${id}-${message.id ?? i}`}>{message.text}</AIChatUserMessage>;
						}
						if (message.role === AIChatRole.Assistant) {
							return (
								<AIChatAssistantMessage key={`${id}-${message.id ?? i}`}>
									{message.contents?.map((content, j) => {
										if (content._type === 'AITextContent') {
											const c = content as AITextContent;
											return (
												<AIMarkdownResponse key={`email-assistant-message${message?.id}-${j}`}>
													{c.text}
												</AIMarkdownResponse>
											);
										}
									})}
								</AIChatAssistantMessage>
							);
						}
						if ((message as AIChatMessageError).error) {
							const errorMessage = message as AIChatMessageError;
							return (
								<AIChatAssistantMessageError
									key={`email-assistant-message-${message.id ?? i}`}
									onTryAgain={() => handleTryAgain(errorMessage.chatRequest)}
								/>
							);
						}
					})}
					{aiAssistantStreamingMutation.isLoading ? (
						<AIChatAssistantMessage>
							<LoadingSpinner type='tiny' />
						</AIChatAssistantMessage>
					) : null}
				</ChatConversationContent>
				<ChatConversationScrollButton />
			</ChatConversation>
			<div className={css(s.inputContainer)}>
				<AssistantChatInput
					streamingInProgress={aiAssistantStreamingMutation.isLoading || aiAssistantStreamingMutation.isStreaming}
					placeholder='Send a message...'
					onSubmit={handlePromptSubmit}
				/>
			</div>
		</PreCallAssistantChatLayout>
	);
}
